import { loadingToast } from '@side/pantry';
import { useState } from 'react';
import { resizeImage } from 'src/components/UploadImageModal/helpers';
import { identityApi } from '../../modules/api-clients/clients';
import { useIdentityMutate } from '../../modules/api-clients/hooks';

export interface UpdateUserAvatarInput {
  file?: string | null;
}

export const avatarErrorsMap: Record<string, string> = {
  unknown: 'Something went wrong. Please try again.',
  '413': 'File size too large, try a smaller file.',
};

export function useUpdateUserAvatar(userId: string) {
  const mutateIdentity = useIdentityMutate();
  const [loading, setLoading] = useState(false);

  const updateAvatar = async (
    updateAvatarInput: UpdateUserAvatarInput,
  ): Promise<boolean> => {
    if (!userId) {
      throw new Error('Cannot create mutation for unknown user');
    }

    const { file } = updateAvatarInput;
    const resizedImage = file ? await resizeImage(file) : null;

    setLoading(true);
    try {
      await loadingToast(
        identityApi
          .PUT('/users/{userId}/avatar', {
            params: { path: { userId } },
            body: { image: resizedImage, x: 0, y: 0 },
          })
          .then(async ({ error, response, data }) => {
            if (error || !response.ok) {
              const status = String(response.status);
              throw new Error(
                avatarErrorsMap[status] ?? avatarErrorsMap.unknown,
              );
            }
            // Optimistically set photoUrl when we have a new headshot;
            // returning undefined falls back to a server revalidate (which
            // also covers the remove-avatar flow). The sidebar avatar reads
            // /auth/userinfo, so update that cache too.
            await Promise.all([
              mutateIdentity(
                ['/users/{userId}', { params: { path: { userId } } }],
                (existingUser) => {
                  if (!existingUser || !data?.headshot) return undefined;
                  return { ...existingUser, photoUrl: data.headshot };
                },
              ),
              mutateIdentity(['/auth/userinfo'], (existingUserinfo) => {
                if (!existingUserinfo || !data?.headshot) return undefined;
                return { ...existingUserinfo, photoUrl: data.headshot };
              }),
            ]);
          }),
        {
          error: (err) =>
            err instanceof Error ? err.message : avatarErrorsMap.unknown,
          loading: 'Updating Avatar',
          success: 'Successfully Updated Avatar',
        },
        { id: 'updateAvatar' },
      );
      return true;
    } catch {
      // Error is already surfaced via the loadingToast error handler.
      // Return false so callers can keep the modal open for retry.
      return false;
    } finally {
      setLoading(false);
    }
  };

  return [updateAvatar, { loading }] as const;
}
